import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Mail, Lock, User } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { AuthenticationService } from "@/modules/identity/services/AuthenticationService";
import { registerSchema, type RegisterFormValues } from "@/modules/identity/schemas/authSchemas";
import { toast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";
import { getFirebaseErrorMessage } from "@/utils/firebaseErrors";

export function RegisterPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({ resolver: zodResolver(registerSchema) });

  async function onSubmit(values: RegisterFormValues) {
    try {
      await AuthenticationService.registerWithEmail(values.nome, values.email, values.password);
      toast({ variant: "success", title: "Conta criada!", description: "Bem-vindo ao Palpiteiro." });
      navigate(ROUTES.home, { replace: true });
    } catch (error) {
      toast({ variant: "destructive", title: "Não foi possível criar sua conta", description: getFirebaseErrorMessage(error) });
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="mx-auto w-full max-w-sm space-y-6 rounded-2xl bg-card p-6 shadow-elevated"
    >
      <div className="space-y-1 text-center">
        <h1 className="font-display text-xl font-bold">Criar conta</h1>
        <p className="text-sm text-muted-foreground">Monte seu bolão em poucos minutos</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="nome">Nome completo</Label>
          <div className="relative">
            <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input id="nome" placeholder="Seu nome" className="pl-10" error={errors.nome?.message} {...register("nome")} />
          </div>
          {errors.nome && <p className="text-xs text-destructive">{errors.nome.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email">E-mail</Label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input id="email" type="email" placeholder="voce@email.com" className="pl-10" error={errors.email?.message} {...register("email")} />
          </div>
          {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Senha</Label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              className="pl-10 pr-10"
              error={errors.password?.message}
              {...register("password")}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirmPassword">Confirmar senha</Label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              className="pl-10"
              error={errors.confirmPassword?.message}
              {...register("confirmPassword")}
            />
          </div>
          {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>}
        </div>

        <div className="flex items-start gap-2">
          <input
            id="aceitaTermos"
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
            {...register("aceitaTermos")}
          />
          <Label htmlFor="aceitaTermos" className="font-normal text-muted-foreground">
            Li e aceito os Termos de Uso e a Política de Privacidade
          </Label>
        </div>
        {errors.aceitaTermos && <p className="-mt-2 text-xs text-destructive">{errors.aceitaTermos.message}</p>}

        <Button type="submit" className="w-full" isLoading={isSubmitting}>
          Criar conta
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        Já tem conta?{" "}
        <Link to={ROUTES.login} className="font-semibold text-primary hover:underline">
          Entrar
        </Link>
      </p>
    </motion.div>
  );
}
